import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Badge, Box, IconButton, Popover, Typography } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';

import {
  AccountCircleLine,
  AccountLineIcon,
  AlertLineIcon,
  CommunityLineIcon,
  HomeIcon,
  Lock2LineIcon,
  LogOutLineIcon,
  Notification2FillIcon,
  RouterFillIcon,
  SimCardLine2,
  StoreLineIcon,
  TechnicianIcon,
  UserLineIcon,
} from '~/assets/icons';

import classNames from 'classnames/bind';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import fiotecRm from '~/assets/images/fiotec-rm-bg.png';
import logoFioTec from '~/assets/images/logo-fiotec.png';
import flagVi from '~/assets/images/flag-vi.svg';
import flagEn from '~/assets/images/flag-en.svg';
import { BaseImage } from '~/components/common';
import { BASE_COLORS } from '~/utils/constants/common';
import styles from './Main.module.scss';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetAuthUserInfo } from '~/thunks/auth/authThunk';
import { notify } from '~/utils/helper';
import _ from 'lodash';
import { adminRouteAbsolute } from '~/utils/constants/route';
import Notification from '~/pages/admin/dashboard/components/Notification';
import usePushNotifications from '~/hooks/usePushNotifications';
import ResetPasswordDialog from '~/pages/admin/dashboard/components/ResetPasswordDialog';

type AppBarProps = MuiAppBarProps & {
  open?: boolean;
};

type TSidebar = {
  id: string;
  name: string;
  icon: ReactNode;
  activeIcon: ReactNode;
  url: string;
};

const sidebarList = [
  {
    id: 'dashboard',
    name: 'Trang chủ',
    icon: <HomeIcon fill={BASE_COLORS.BLUE_BOLD} />,
    activeIcon: <HomeIcon fill='white' className='lg:w-[20px] lg:h-[21px] xl:w-[24px] xl:h-[24px]' />,
    url: '/admin/dashboard',
  },
  {
    id: 'device',
    name: 'Thiết bị',
    icon: <RouterFillIcon fill={BASE_COLORS.BLUE_BOLD} className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    activeIcon: <RouterFillIcon fill='white' className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    url: '/admin/equipment',
  },
  {
    id: 'location',
    name: 'Cơ sở',
    icon: (
      <CommunityLineIcon fill={BASE_COLORS.BLUE_BOLD} className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />
    ),
    activeIcon: <CommunityLineIcon fill='white' className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    url: '/admin/facilities',
  },
  {
    id: 'user',
    name: 'Người dùng',
    icon: <UserLineIcon fill={BASE_COLORS.BLUE_BOLD} className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    activeIcon: <UserLineIcon fill='white' className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    url: '/admin/users',
  },
  {
    id: 'agent',
    name: 'Đại lý',
    icon: <StoreLineIcon fill={BASE_COLORS.BLUE_BOLD} className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    activeIcon: <StoreLineIcon fill='white' className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    url: '/admin/agents',
  },
  {
    id: 'distributor',
    name: 'Nhà phân phối',
    icon: <SimCardLine2 fill={BASE_COLORS.BLUE_BOLD} className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    activeIcon: <SimCardLine2 fill='white' className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    url: '/admin/distributors',
  },
  {
    id: 'warning',
    name: 'Cảnh báo',
    icon: <AlertLineIcon fill={BASE_COLORS.BLUE_BOLD} className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    activeIcon: <AlertLineIcon fill='white' className='lg:w-[20px] lg:h-[20px] xl:w-[24px] xl:h-[24px]' />,
    url: '/admin/alerts',
  },
  {
    id: 'technician',
    name: 'Kỹ thuật viên',
    icon: <TechnicianIcon fill={BASE_COLORS.BLUE_BOLD} className='lg:w-[17px] lg:h-[17px] xl:w-[20px] xl:h-[20px]' />,
    activeIcon: <TechnicianIcon fill='white' className='lg:w-[17px] lg:h-[17px] xl:w-[20px] xl:h-[20px]' />,
    url: '/admin/technicians',
  },
];

const cx = classNames.bind(styles);

const drawerWidth = 0;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function MiniDrawer() {
  const { t } = useTranslation(['admin/layout']);

  const location = useLocation();

  const [open] = useState<boolean | undefined>(true);
  const [activeScreen, setActiveScreen] = useState<string>('home');
  const { isLoading } = useAppSelector((state) => state.common);
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { authUser } = useAppSelector((state) => state.auth);

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [anchorElement, setAnchorElement] = React.useState<HTMLButtonElement | null>(null);
  const [language, setLanguage] = useState<string>('vi');

  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState<boolean>(false);

  usePushNotifications();

  useEffect(() => {
    const matchedScreen = sidebarList.find((screen) => location.pathname.indexOf(screen.url) > -1);

    if (location.pathname.includes(adminRouteAbsolute.profile)) {
      sidebarList.push({
        id: 'profile',
        name: 'Thông tin tài khoản',
        icon: <></>,
        activeIcon: <></>,
        url: '',
      });
    }

    if (matchedScreen) setActiveScreen(matchedScreen.id);
  }, [location]);

  useEffect(() => {
    dispatch(handleGetAuthUserInfo());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickNotification = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const changeLanguage = () => {
    if (language === 'en') return setLanguage('vi');
    setLanguage('en');
    notify('success', 'Sắp ra mắt');
  };

  const handleCloseNotification = () => {
    setAnchorElement(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openUser = Boolean(anchorEl);
  const id = openUser ? 'simple-popover' : undefined;

  const openNotification = Boolean(anchorElement);
  const idNotification = openNotification ? 'simple-popover' : undefined;

  const permissionSidebar: Array<TSidebar> = useMemo(() => {
    const permissionItems: Array<TSidebar> = [];
    sidebarList.forEach((sidebar) => {
      const permissionItem: TSidebar = _.clone(sidebar);
      let hasPermission: boolean = false;

      if (authUser?.role_screens) {
        hasPermission = authUser.role_screens.filter((item) => item === sidebar.id)?.length > 0;
      }

      if (hasPermission) permissionItems.push(permissionItem);
    });
    return permissionItems;
  }, [authUser]);

  const handleClickAccountInfo = () => {
    navigate(adminRouteAbsolute.profile);
    handleClose();
  };

  const handleClickChangePassword = () => {
    setOpenResetPasswordDialog(true);
    setAnchorEl(null);
  };

  return (
    <Box className='w-full'>
      <Box className={cx('layout-main')}>
        {isLoading && <Box className='h-full w-full z-[100000] opacity-0 cursor-wait absolute top-0 left-0'></Box>}
        <AppBar position='fixed' open={open} color='default'>
          <Box
            className={cx('flex justify-between px-[40px] lg:gap-[16px] xl:gap-[24px] h-[71px] mt-[2px]', {
              'pl-0 not-open': !open,
            })}
          >
            <Box className='flex relative flex-row items-end gap-3 w-[185px]'>
              <BaseImage src={logoFioTec} className='xl:h-[54px] lg:h-[40px]' />
              <BaseImage
                src={fiotecRm}
                className=' absolute xl:h-[102px]  lg:h-[60px]  left-2/3 top-[-66px] translate-y-1/2    -translate-x-1/2   z-10 '
              />
            </Box>
            <Box className='flex justify-center items-center sidebarButton flex-grow'>
              {permissionSidebar.map((item, index) => {
                return (
                  <React.Fragment key={index}>
                    <Box
                      className={cx(
                        'flex gap-1 items-center h-full flex-1 cur cursor-pointer justify-center',
                        `${item.id === activeScreen ? 'active-nav' : 'non-ative-nav'}`
                      )}
                      key={item.id}
                      onClick={(e) => {
                        navigate(item.url);
                      }}
                    >
                      {activeScreen !== item.id ? item.icon : item.activeIcon}
                      <Typography
                        className='md:text-[8px] lg:text-[10px] xl:text-[12px] font-bold whitespace-nowrap'
                        sx={{ color: item.id !== activeScreen ? BASE_COLORS.BLUE_BOLD : 'white' }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
            <Box sx={{ gap: 1 }} className={cx('right-toolbar')}>
              <IconButton className={cx('badge-button')} onClick={handleClickNotification}>
                <Badge color='info' className={cx('notification-badge')}>
                  <Notification2FillIcon fill='#ED1002' />
                </Badge>
              </IconButton>
              <IconButton aria-describedby={id} size='large' className={cx('badge-button')} onClick={handleClick}>
                <AccountCircleLine fill='#ED1002' />
              </IconButton>
              <IconButton
                aria-describedby={undefined}
                size='large'
                className={cx('badge-button')}
                onClick={changeLanguage}
              >
                {language === 'vi' ? (
                  <BaseImage src={flagVi} className='xl:h-[20px] lg:h-[20px] cursor-pointer' />
                ) : null}
                {language === 'en' ? (
                  <BaseImage src={flagEn} className='xl:h-[20px] lg:h-[20px] cursor-pointer' />
                ) : null}
              </IconButton>
            </Box>
          </Box>
          <Box className='h-[44px] relative bg-[#005F73] mt-[2px] flex items-center px-[40px]'>
            <Typography className='text-white font-bold lg:text-base xl:text-[20px]'>
              {sidebarList.find((item) => {
                return item.id === activeScreen;
              })?.name ?? ''}
            </Typography>
          </Box>
          <Popover
            id={id}
            open={openUser}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            slotProps={{
              paper: {
                sx: {
                  mt: '25px',
                  width: '211px',
                  borderRadius: '12px',
                  boxShadow: '5px 4px 10px 3px #00000040',
                },
              },
            }}
            sx={{ zIndex: 9999 }}
          >
            <Box>
              <Box
                className='w-full h-[45px] flex items-center gap-[20px] px-[23px] hover:cursor-pointer hover:bg-slate-100'
                onClick={handleClickAccountInfo}
              >
                <AccountLineIcon />
                <Box>{t('header.accountInfo')}</Box>
              </Box>
              <Box
                className='w-full h-[45px] flex items-center gap-[20px] px-[23px] hover:cursor-pointer hover:bg-slate-100'
                onClick={handleClickChangePassword}
              >
                <Lock2LineIcon fill='black' />
                <Box>{t('header.changePassword')}</Box>
              </Box>
              <Box
                className='w-full h-[45px] flex items-center gap-[20px] px-[23px] hover:cursor-pointer hover:bg-slate-100'
                onClick={() => {
                  navigate('/admin/login');
                }}
              >
                <LogOutLineIcon />
                <Box>{t('header.logout')}</Box>
              </Box>
            </Box>
          </Popover>
          <Popover
            id={idNotification}
            open={openNotification}
            anchorEl={anchorElement}
            onClose={handleCloseNotification}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            slotProps={{
              paper: {
                sx: {
                  mt: '25px',
                  width: '425px',
                  borderRadius: '12px',
                  border: '1px solid #005F73',
                },
              },
            }}
          >
            <Notification onSetAnchorElement={setAnchorElement} />
          </Popover>
        </AppBar>
        <Box
          component='main'
          className={`${cx('main')} ${
            location.pathname.includes(adminRouteAbsolute.profile) ? 'bg-[#EEEEEE]' : 'bg-white'
          }`}
        >
          <Outlet />
        </Box>
      </Box>
      <ResetPasswordDialog open={openResetPasswordDialog} setOpen={setOpenResetPasswordDialog} />
    </Box>
  );
}
